import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MessageComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/message/message.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { EDeliverableType } from '@enums/campaign.enum';
import { IMessageAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { TextPostAnalyticsBaseDirective } from '../text-post-analytics-base/text-post-analytics-base.directive';

@Component({
  imports: [TranslateModule, MatButtonModule, MessageComponent, LowerCasePipe],
  providers: [DeliverableFindingsStore],
  selector: 'app-message-post-analytics',
  templateUrl: './message-post-analytics.component.html',
})
export class MessagePostAnalyticsComponent extends TextPostAnalyticsBaseDirective<undefined, IMessageAnalytics> {
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'message-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPoint.totalCount'),
    value: 0,
  };
  public override readonly deliverableType = EDeliverableType.message;

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.messages?.find((c) => c.deliverableId === deliverableId);
    }

    return null;
  });

  public override readonly contentData = computed(() => {
    const socialContent = this.executionAnalyticsWithFindings()?.content?.socialContent;

    if (!socialContent) {
      return null;
    }

    if ('facebookData' in socialContent) {
      return socialContent.facebookData;
    }

    if ('xData' in socialContent) {
      return socialContent.xData;
    }

    return null;
  });

  constructor() {
    super();
  }

  protected override setKeywordCounts(): void {
    const message = this.currentDeliverable();

    const keywords = message?.keywords?.map((k) => k.keyword) ?? [];
    const messageText = this.contentData()?.message ?? '';
    this.keywordCounts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywords, [messageText]);

    const total = Object.values(this.keywordCounts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), this.keywordCounts);
  }

  protected override setKeywordCardList(): void {
    const message = this.currentDeliverable();

    if (message?.keywords && message.keywords.length) {
      const kpiCardList = message.keywords.map((k) => {
        return {
          icon: 'keyword.svg',
          id: `${k.keyword}-kpi-card`,
          keyword: k.keyword,
          value: this.keywordCounts[k.keyword] ?? 0,
        };
      });

      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override setKeywordsEffect(): void {
    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        this.keywords = this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
      }
    });
  }
}
